import type { TShared, TSharedKey } from 'translations'

import type { MultiSelectAdaptiveProps as Props } from '../MultiSelectAdaptive'
import { SelectAllOptionValue } from './SelectAllValueOption'

/**
 * @description
 * If empty selection, fallback to using the placeholder.
 * If one option selected, use the `.label` of the option. (e.g. My Site)
 * If many options selected, use the pluralised/count label (e.g. 10 Sites).
 *
 * @example
 * ```ts
 * const tShared = useTranslations(translationsShared)
 * <MultiSelectAdaptive
 *   // ...
 *   valueLabel={MultiSelectAdaptive.valueLabel.Standard(tShared, 'nSites')}
 *   // ...
 * />
 * ```
 */
export const ValueLabelDefault =
  <TOption extends Props.Option>({
    tShared,
    allKey,
    manyKey,
    noneKey,
  }: {
    tShared: TShared
    allKey: TSharedKey
    manyKey: TSharedKey<'count'>
    noneKey?: TSharedKey
  }): NonNullable<Props<TOption>['valueLabel']> =>
  ({ value, optionsMap }) => {
    if (value === SelectAllOptionValue) {
      return tShared(allKey)
    }
    if (!value?.length) {
      return noneKey ? tShared(noneKey) : undefined
    }
    if (value.length === 1) {
      const option = optionsMap.get(value[0])
      if (option) {
        return option.label
      }
    }
    return tShared(manyKey, { count: `${value.length}` })
  }
